import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Modal,
  Accordion,
  AccordionItem,
  Checkbox,
  Search,
  Loading,
  Tooltip,
} from '@carbon/react';
import { ToolsAlt } from '@carbon/react/icons';

import { savePreferences } from './api';

/* -------- shared constants (export for Assistant if needed) ---------- */

export const BASE_FAMILIES = {
  ffai_search: {
    label: 'Fresh Focus AI Search',
    tools: {
      web_search_google: 'Google web search',
      web_search_tavily: 'Tavily web search',
      web_search_duckduckgo: 'DuckDuckGo search',
      news_search_google: 'Google news',
      news_search_duckduckgo: 'DuckDuckGo news',
      medical_search_pubmed: 'PubMed medical',
      scholarly_search_google: 'Google Scholar',
      reddit_search: 'Reddit search',
      jobs_search_google: 'Google jobs',
      web_trends_search_google: 'Google Trends',
      finance_news_search_yahoo: 'Yahoo finance news',
      finance_news_search_google: 'Google finance news',
      finance_news_search_alphavantage: 'AlphaVantage finance',
      video_search_youtube: 'YouTube search',
      programming_search_stackoverflow: 'Stack Overflow',
      encyclopedia_search_wikipedia: 'Wikipedia search',
    },
  },
  ffai_flights: {
    label: 'Flight Search',
    tools: {
      search_flights: 'Search flight offers',
      search_airports: 'Search airports by city',
      get_cheapest_flight_dates: 'Find cheapest travel dates',
      get_flight_destinations: 'Get destination ideas',
    },
  },
  ffai_hotels: {
    label: 'Hotel Search (Amadeus)',
    tools: {
      search_hotels_by_city: 'Search hotels in a city with prices',
      search_hotels_by_location: 'Find hotels near coordinates',
      list_hotels_in_city: 'List hotels without prices',
    },
  },
  ffai_hotels_booking: {
    label: 'Hotel Search (Booking.com)',
    tools: {
      booking_search_hotels:
        'Search hotels with better inventory (50/month limit)',
      booking_search_destination: 'Find destination IDs for hotel search',
    },
  },
  ffai_local_files: {
    label: 'Local files (on Files tab)',
    tools: {
      save_data_to_file: 'Save data to local file',
      retrieve_data_from_file: 'Retrieve data from local file',
      list_files: 'List local files',
      delete_file: 'Delete local file',
    },
  },
  ffai_web: {
    label: 'Fresh Focus Web',
    tools: {
      get_web_page: 'Get web page (text + meta)',
      get_web_page_and_links: 'Get page + links (depth 2)',
    },
  },
  ffai_code: {
    label: 'Code Execution',
    tools: {
      execute_code: 'Run Python, Node.js, or Bash in an isolated sandbox',
    },
  },
};

/* 2) Integration → REST endpoint meta                                    */
const INTEGRATION_META = {
  ffai_email: {
    label: 'Email',
    status: async () => ({ isConnected: true }), // email always on
  },
  ffai_sms: {
    label: 'SMS',
    status: async hdr => {
      const txt = await fetch(
        '/mlapi/premium/task/data/get/configuration/phone_number',
        { headers: hdr }
      ).then(r => (r.ok ? r.text() : ''));
      return { isConnected: !!txt.trim() };
    },
  },
  ffai_evernote: {
    label: 'Evernote',
    status: hdr =>
      fetch('/api/premium/evernote/status', { headers: hdr }).then(r =>
        r.json()
      ),
  },
  ffai_github: {
    label: 'GitHub',
    status: hdr =>
      fetch('/mlapi/premium/integration/github/status', {
        headers: hdr,
      }).then(r => r.json()),
  },
  ffai_s3: {
    label: 'S3',
    status: hdr =>
      fetch('/mlapi/premium/integration/s3/status', { headers: hdr }).then(r =>
        r.json()
      ),
  },
  ffai_gdrive: {
    label: 'Google Drive',
    status: hdr =>
      fetch('/mlapi/premium/integration/google-drive/status', {
        headers: hdr,
      }).then(r => r.json()),
  },
  // TODO: ffai_x_twitter temporarily hidden — Twitter API changed, pending reimplementation
  ffai_a2a: {
    label: 'Agent-to-Agent (A2A)',
    status: hdr =>
      fetch('/mlapi/premium/integration/a2a/status', {
        headers: hdr,
      }).then(r => r.json()),
  },
  ffai_openclaw: {
    label: 'OpenClaw / NemoClaw',
    status: hdr =>
      fetch('/mlapi/premium/integration/openclaw/status', {
        headers: hdr,
      }).then(r => r.json()),
  },
};

const INTEGRATION_TOOLS = {
  ffai_email: {
    send_email: 'Send e-mail',
  },
  ffai_sms: {
    send_sms_text_message: 'Send SMS text message',
  },
  ffai_evernote: {
    list_evernote_notebooks: 'List notebooks',
    list_evernote_notes_in_notebook: 'List notes in notebook',
    get_evernote_note: 'Get note',
    create_evernote_note: 'Create note',
    update_evernote_note: 'Update note',
    delete_evernote_note: 'Delete note',
  },
  ffai_github: {
    list_github_repos: 'List repositories',
    list_github_tree: 'List repo tree',
    create_github_branch: 'Create branch',
    list_github_commits: 'List commits',
    create_github_pull_request: 'Create pull request',
    create_github_file: 'Create file',
    update_github_file: 'Update file',
    get_github_file: 'Get file',
    comment_on_github_issue: 'Comment on issue',
    create_github_issue: 'Create issue',
    delete_github_file: 'Delete file',
    list_github_branches: 'List branches',
    list_github_issues: 'List issues',
    list_github_pull_requests: 'List pull requests',
    merge_github_pull_request: 'Merge pull request',
    list_github_files: 'List files',
  },
  ffai_s3: {
    list_s3_files: 'List S3 files',
    get_s3_file: 'Get S3 file',
    upload_s3_file: 'Upload S3 file',
    delete_s3_file: 'Delete S3 file',
    generate_s3_presigned_url: 'Generate presigned URL',
  },
  ffai_gdrive: {
    list_gdrive_files: 'List Drive files',
    read_gdrive_file: 'Read Drive file',
    create_gdrive_file: 'Create Drive file',
    update_gdrive_file: 'Update Drive file',
    delete_gdrive_file: 'Delete Drive file',
    search_gdrive_files: 'Search Drive files',
    create_gdrive_folder: 'Create Drive folder',
  },
  ffai_a2a: {
    list_a2a_agents: 'List registered A2A agents',
    call_a2a_agent: 'Call a registered A2A agent',
  },
  ffai_openclaw: {
    list_openclaw_computers: 'List registered OpenClaw computers',
    send_openclaw_task: 'Send a task to an OpenClaw computer',
  },
  // TODO: ffai_x_twitter tools temporarily hidden — Twitter API changed, pending reimplementation
};

/* -------------------------------------------------------------------- */

const ToolSelectorModal = ({
  token,
  selectedTools,
  setSelectedTools,
  selectedFamilies,
  setSelectedFamilies,
  mcpServers /* array of {id,name} objects */,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [catalogue, setCatalogue] = useState(BUILTIN_FAMILIES);
  const [loading, setLoading] = useState(true);

  const [tmpTools, setTmpTools] = useState(selectedTools);
  const [tmpFamilies, setTmpFamilies] = useState(selectedFamilies);

  /* remember catalogue keys from *last* time the modal was opened */
  const prevCatalogueRef = useRef(new Set()); // initially empty

  /* whenever modal opens copy *current* selections → locals */
  const openModal = () => {
    setTmpTools(selectedTools);
    setTmpFamilies(selectedFamilies);
    setOpen(true);
  };

  /* fetch MCP catalogues once */

  /* rebuild catalogue each time the modal opens */
  useEffect(() => {
    if (!open) return; // run only when modal is visible
    (async () => {
      setLoading(true);
      const hdr = { Authorization: `Bearer ${token}` };

      /* 1) start with fixed search families */
      let cat = { ...BASE_FAMILIES };

      /* 2) probe each integration in parallel */
      const results = await Promise.all(
        Object.entries(INTEGRATION_META).map(async ([famKey, meta]) => {
          try {
            return [famKey, await meta.status(hdr)];
          } catch {
            return [famKey, { isConnected: false }];
          }
        })
      );

      /* 3) add family only if user connected */
      results.forEach(([famKey, st]) => {
        if (!st.isConnected) return;
        cat[famKey] = {
          label: INTEGRATION_META[famKey].label,
          tools: INTEGRATION_TOOLS[famKey] || {},
        };
      });

      /* 4) merge in existing MCP families (code you already had) */
      if (mcpServers?.length) {
        const hdr2 = { Authorization: `Bearer ${token}` };
        for (const { id, name } of mcpServers) {
          const tools = await fetch(`/mlapi/premium/mcp-servers/${id}/tools`, {
            headers: hdr2,
          }).then(r => r.json());
          tools.forEach(t => {
            const famKey = `mcp_${id}_${t.family}`;
            if (!cat[famKey]) {
              cat[famKey] = { label: `${t.family} (${name})`, tools: {} };
            }
            cat[famKey].tools[t.name] = t.description || t.name;
          });
        }
      }

      setCatalogue(cat);
      prevCatalogueRef.current = new Set(Object.keys(cat));
      const stillValidToolIds = new Set(
        Object.values(cat).flatMap(f => Object.keys(f.tools))
      );
      const stillValidFams = new Set(Object.keys(cat));

      setSelectedTools(prev => prev.filter(id => stillValidToolIds.has(id)));
      setSelectedFamilies(prev => prev.filter(f => stillValidFams.has(f)));

      /* 5) auto-select integration families that have *just appeared* —
            i.e. not in the previous catalogue, never auto-tick search */
      const prevKeys = prevCatalogueRef.current;
      const newIntegrationFams = Object.keys(cat).filter(
        f =>
          !prevKeys.has(f) && // first time we see it
          f !== 'ffai_search' && // don’t auto-tick search
          f.startsWith('ffai_') && // FFAI integrations
          INTEGRATION_TOOLS[f] // has tool list
      );
      if (newIntegrationFams.length) {
        const newTools = newIntegrationFams.flatMap(f =>
          Object.keys(cat[f].tools)
        );
        setTmpFamilies(prev => [...prev, ...newIntegrationFams]);
        setTmpTools(prev => [...prev, ...newTools]);
      }

      setLoading(false);
    })();
  }, [open]); // runs each time the modal is opened

  // keep local state in sync when the modal is *closed*
  useEffect(() => {
    if (!open) {
      setTmpTools(selectedTools);
      setTmpFamilies(selectedFamilies);
    }
  }, [selectedTools, selectedFamilies, open]);

  /* helpers ----------------------------------------------------------- */

  /* ============== toggle helpers now use tmpTools/tmpFamilies ============== */
  const toggleFamily = famKey => {
    const toolIds = Object.keys(catalogue[famKey]?.tools || []);
    if (!toolIds.length) return;

    const selectedCnt = toolIds.filter(id => tmpTools.includes(id)).length;
    const willSelectAll = selectedCnt < toolIds.length;

    setTmpTools(prev => {
      return willSelectAll
        ? Array.from(new Set([...prev, ...toolIds]))
        : prev.filter(id => !toolIds.includes(id));
    });

    setTmpFamilies(prev => {
      if (willSelectAll) return Array.from(new Set([...prev, famKey]));
      return prev.filter(f => f !== famKey);
    });
  };

  const toggleTool = toolId => {
    setTmpTools(prev => {
      const exists = prev.includes(toolId);
      const next = exists ? prev.filter(t => t !== toolId) : [...prev, toolId];

      /* sync family checkbox */
      setTmpFamilies(prevFams => {
        const upd = new Set(prevFams);
        for (const [famKey, fam] of Object.entries(catalogue)) {
          if (!fam.tools[toolId]) continue;
          const ids = Object.keys(fam.tools);
          const allOk = ids.every(id =>
            id === toolId ? !exists : next.includes(id)
          );
          if (allOk) upd.add(famKey);
          else upd.delete(famKey);
          break;
        }
        return Array.from(upd);
      });

      return next;
    });
  };

  const filterLower = filter.toLowerCase();
  /* show committed count when closed, live count when open */
  const totalSel = open ? tmpTools.length : selectedTools.length;
  const btnLabel = totalSel ? `Tools (${totalSel})` : 'Tools';

  /* ───────── modal footer callbacks ───────── */
  const handleOk = () => {
    setSelectedTools(tmpTools);
    setSelectedFamilies(tmpFamilies);
    setOpen(false);
    savePreferences(token, {
      tools: { selected_tools: tmpTools, selected_families: tmpFamilies },
    }).catch(console.error);
  };

  const handleCancel = () => {
    /* discard edits */
    setTmpTools(selectedTools);
    setTmpFamilies(selectedFamilies);
    setOpen(false);
  };

  /* 🔄  prune MCP families/tools as soon as the server list changes */
  useEffect(() => {
    const validIds = new Set(mcpServers.map(s => s.id));
    const famsToDrop = selectedFamilies.filter(
      f => f.startsWith('mcp_') && !validIds.has(f.split('_')[1])
    );
    if (!famsToDrop.length) return;

    const dropPrefix = new RegExp(`^(${famsToDrop.join('|')})_?`);
    setSelectedFamilies(prev => prev.filter(f => !famsToDrop.includes(f)));
    setSelectedTools(prev => prev.filter(t => !dropPrefix.test(t)));
  }, [mcpServers]); // ← runs whenever a server is removed/added

  /* ------------------------------------------------------------------ */
  return (
    <div className="tool-selector-modal">
      <Tooltip label="Choose which tools the AI may use" align="bottom">
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ToolsAlt}
          onClick={openModal}>
          {btnLabel}
        </Button>
      </Tooltip>

      <Modal
        open={open}
        size="md"
        modalHeading="Select tools"
        primaryButtonText="OK"
        secondaryButtonText="Cancel"
        onRequestClose={handleCancel} /* × icon or escape */
        onRequestSubmit={handleOk} /* OK button */
        className="tool-selector-window">
        {loading ? (
          <Loading active small withOverlay={false} />
        ) : (
          <>
            {/* helper banner */}
            <p className="modal-hint">
              Expand a category to see its tools. Tick the category checkbox to
              select or clear all tools, or tick individual tools inside. Your
              selection applies to every question you ask until you change it.
            </p>
            <p>&nbsp;</p>

            <Search
              size="sm"
              light
              id="tool-filter"
              placeholder="Filter tools…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />

            <Accordion
              style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 8 }}>
              {Object.entries(catalogue).map(([famKey, fam]) => {
                const toolIds = Object.keys(fam.tools);
                const filtered = toolIds.filter(
                  id =>
                    !filter ||
                    id.toLowerCase().includes(filterLower) ||
                    fam.tools[id].toLowerCase().includes(filterLower)
                );
                if (!filtered.length) return null;

                const allSel = toolIds.every(id => tmpTools.includes(id));
                const someSel = toolIds.some(id => tmpTools.includes(id));

                const Header = (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div onClick={e => e.stopPropagation()}>
                      <Checkbox
                        id={`fam-${famKey}`}
                        hideLabel
                        labelText=""
                        checked={allSel}
                        indeterminate={someSel && !allSel}
                        onChange={() => toggleFamily(famKey)}
                      />
                    </div>
                    <span>{fam.label}</span>
                  </div>
                );

                return (
                  <AccordionItem key={famKey} title={Header}>
                    {filtered.map(tid => (
                      <Checkbox
                        key={tid}
                        id={tid}
                        className="tool-checkbox"
                        labelText={fam.tools[tid]}
                        checked={tmpTools.includes(tid)}
                        onChange={() => toggleTool(tid)}
                      />
                    ))}
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        )}
      </Modal>
    </div>
  );
};

/* ---------------------------------------------------------- */
/*  helper exports for other components                       */

/* all fixed “built-in” family definitions (search + integrations) */
export const BUILTIN_FAMILIES = {
  ...BASE_FAMILIES,
  ...Object.fromEntries(
    Object.entries(INTEGRATION_TOOLS).map(([k, tools]) => [
      k,
      { label: INTEGRATION_META[k]?.label || k, tools },
    ])
  ),
};

/* flat Set of every built-in tool-id — handy for filtering */
export const BUILTIN_TOOL_IDS = new Set(
  Object.values(BUILTIN_FAMILIES).flatMap(f => Object.keys(f.tools))
);

export default ToolSelectorModal;
